export interface IdleMotion {
  readonly blink: number;
  readonly headX: number;
  readonly headY: number;
  readonly gazeX: number;
  readonly gazeY: number;
}

interface ParameterBinding {
  readonly group: 'mouth' | 'face';
  readonly index: number;
}

const UINT32_MAX = 0xffff_ffff;
const BLINK_BLOCK_SECONDS = 4;
const BLINK_JITTER_SECONDS = 2;
const BLINK_DURATION_SECONDS = 0.18;
const GAZE_BLOCK_SECONDS = 7;

export function idleMotion(elapsedSeconds: number, seed: number): IdleMotion {
  const elapsed = finiteNonNegative(elapsedSeconds);
  const normalizedSeed = uint32(seed);
  const [gazeX, gazeY] = gaze(elapsed, normalizedSeed);
  return {
    blink: blink(elapsed, normalizedSeed),
    headX: 0.04 * smoothNoise(elapsed, normalizedSeed, 1.9, 101),
    headY: 0.03 * smoothNoise(elapsed, normalizedSeed, 2.3, 103),
    gazeX,
    gazeY,
  };
}

export function applyIdleMotion(
  face: ArrayLike<number>,
  parameterBindings: ReadonlyMap<string, ParameterBinding>,
  elapsedSeconds: number,
  seed: number,
): Float32Array {
  const values = Float32Array.from(face, (value) => Number.isFinite(value) ? clamp(value) : 0);
  const motion = idleMotion(elapsedSeconds, seed);
  maxParameter(values, parameterBindings, 'face.blinkLeft', motion.blink);
  maxParameter(values, parameterBindings, 'face.blinkRight', motion.blink);
  addParameter(values, parameterBindings, 'face.headX', motion.headX);
  addParameter(values, parameterBindings, 'face.headY', motion.headY);
  addParameter(values, parameterBindings, 'face.gazeX', motion.gazeX);
  addParameter(values, parameterBindings, 'face.gazeY', motion.gazeY);
  return values;
}

function blink(elapsed: number, seed: number): number {
  const block = Math.floor(elapsed / BLINK_BLOCK_SECONDS);
  return Math.max(...[block - 1, block].map((candidate) => pulse(
    elapsed,
    candidate * BLINK_BLOCK_SECONDS + BLINK_JITTER_SECONDS * unit(seed, candidate, 11),
    BLINK_DURATION_SECONDS,
  )));
}

function gaze(elapsed: number, seed: number): [number, number] {
  const block = Math.floor(elapsed / GAZE_BLOCK_SECONDS);
  for (const candidate of [block - 1, block]) {
    if (unit(seed, candidate, 31) >= 0.32) continue;
    const start = candidate * GAZE_BLOCK_SECONDS + 1 + 3.5 * unit(seed, candidate, 37);
    const duration = 0.8 + 0.8 * unit(seed, candidate, 41);
    const envelope = pulse(elapsed, start, duration);
    if (envelope <= 0) continue;
    const direction = unit(seed, candidate, 43) < 0.5 ? -1 : 1;
    const horizontal = direction * (0.18 + 0.12 * unit(seed, candidate, 47));
    const vertical = (unit(seed, candidate, 53) * 2 - 1) * 0.12;
    return [horizontal * envelope, vertical * envelope];
  }
  return [0, 0];
}

function smoothNoise(elapsed: number, seed: number, period: number, salt: number): number {
  const position = elapsed / period;
  const index = Math.floor(position);
  const phase = position - index;
  const eased = phase * phase * (3 - 2 * phase);
  const left = unit(seed, index, salt) * 2 - 1;
  const right = unit(seed, index + 1, salt) * 2 - 1;
  return left + (right - left) * eased;
}

function pulse(elapsed: number, start: number, duration: number): number {
  const phase = (elapsed - start) / duration;
  if (phase < 0 || phase > 1) return 0;
  return Math.sin(Math.PI * phase) ** 2;
}

function unit(seed: number, index: number, salt: number): number {
  let value = (
    uint32(seed)
    + Math.imul((uint32(index) + 1) >>> 0, 0x9e37_79b9)
    + Math.imul(uint32(salt), 0x85eb_ca6b)
  ) >>> 0;
  value = (value ^ (value >>> 16)) >>> 0;
  value = Math.imul(value, 0x7feb_352d) >>> 0;
  value = (value ^ (value >>> 15)) >>> 0;
  value = Math.imul(value, 0x846c_a68b) >>> 0;
  value = (value ^ (value >>> 16)) >>> 0;
  return value / UINT32_MAX;
}

function addParameter(
  values: Float32Array,
  bindings: ReadonlyMap<string, ParameterBinding>,
  name: string,
  amount: number,
): void {
  const binding = bindings.get(name);
  if (binding?.group === 'face') values[binding.index] = clamp((values[binding.index] ?? 0) + amount);
}

function maxParameter(
  values: Float32Array,
  bindings: ReadonlyMap<string, ParameterBinding>,
  name: string,
  amount: number,
): void {
  const binding = bindings.get(name);
  if (binding?.group === 'face') values[binding.index] = Math.max(values[binding.index] ?? 0, clamp(amount));
}

function finiteNonNegative(value: number): number {
  if (!Number.isFinite(value) || value < 0) throw new Error('elapsedSeconds must be a finite non-negative number');
  return value;
}

function uint32(value: number): number {
  if (!Number.isSafeInteger(value)) throw new Error('idle seed must be a safe integer');
  return value >>> 0;
}

function clamp(value: number): number {
  return Math.min(1, Math.max(0, value));
}
